/**
 * Role Detail Page
 *
 * Create or edit a role with policies and permissions
 */

import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import {
    ArrowLeft,
    Shield,
    Save,
    Loader2,
    Plus,
    X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import {
    useRole,
    useRoles,
    useCreateRole,
    useUpdateRole,
    usePolicies,
    useAccessEntries,
    useCreateAccess,
    useDeleteAccess,
} from '@/hooks/useAccessControl';
import { getPolicyId } from '@/stores/access-control';
import { toast } from '@/components/ui/Toast';

interface RoleFormData {
    name: string;
    icon: string;
    description: string;
    parent: string | null;
}

export function RoleDetailPage() {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const isNew = id === 'new';

    // Data fetching
    const { data: role, isLoading: roleLoading } = useRole(isNew ? undefined : id);
    const { data: roles } = useRoles();
    const { data: policies } = usePolicies();
    const { data: accessEntries } = useAccessEntries(isNew ? undefined : { role: id });

    // Mutations
    const createRole = useCreateRole();
    const updateRole = useUpdateRole();
    const createAccess = useCreateAccess();
    const deleteAccess = useDeleteAccess();

    // Form state
    const [formData, setFormData] = useState<RoleFormData>({
        name: '',
        icon: 'supervised_user_circle',
        description: '',
        parent: null,
    });
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [showPolicyModal, setShowPolicyModal] = useState(false);

    // Load existing role data
    useEffect(() => {
        if (role) {
            setFormData({
                name: role.name,
                icon: role.icon,
                description: role.description || '',
                parent: role.parent,
            });
        }
    }, [role]);

    // Available parent roles (exclude current role and its descendants)
    const availableParents = roles?.filter((r) => {
        if (isNew) return true;
        if (r.id === id) return false;
        // Check if r is a descendant of current role
        let current = r;
        while (current.parent) {
            if (current.parent === id) return false;
            const parent = roles?.find((p) => p.id === current.parent);
            if (!parent) break;
            current = parent;
        }
        return true;
    });

    // Policies linked to this role
    const linkedPolicies = policies?.filter((p) =>
        accessEntries?.some((a) => getPolicyId(a) === p.id)
    );

    // Available policies to add
    const availablePolicies = policies?.filter(
        (p) => !accessEntries?.some((a) => getPolicyId(a) === p.id)
    );

    const validateForm = (): boolean => {
        const newErrors: Record<string, string> = {};
        if (!formData.name.trim()) {
            newErrors.name = 'Name is required';
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!validateForm()) return;

        try {
            if (isNew) {
                const newRole = await createRole.mutateAsync({
                    name: formData.name,
                    icon: formData.icon,
                    description: formData.description || undefined,
                    parent: formData.parent,
                });
                toast.success('Role created successfully');
                navigate(`/admin/settings/roles/${newRole.id}`, { replace: true });
            } else if (id) {
                await updateRole.mutateAsync({
                    id,
                    data: {
                        name: formData.name,
                        icon: formData.icon,
                        description: formData.description || undefined,
                        parent: formData.parent,
                    },
                });
                toast.success('Role saved successfully');
            }
        } catch (err) {
            console.error('Failed to save role:', err);
            toast.error('Failed to save role');
        }
    };

    const handleAddPolicy = async (policyId: string) => {
        if (!id || isNew) return;
        try {
            await createAccess.mutateAsync({
                role: id,
                policy: policyId,
            });
            setShowPolicyModal(false);
        } catch (err) {
            console.error('Failed to add policy:', err);
        }
    };

    const handleRemovePolicy = async (accessId: string) => {
        try {
            await deleteAccess.mutateAsync(accessId);
        } catch (err) {
            console.error('Failed to remove policy:', err);
        }
    };

    const isSaving = createRole.isPending || updateRole.isPending;

    if (!isNew && roleLoading) {
        return (
            <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center gap-4">
                <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => navigate('/admin/settings/roles')}
                >
                    <ArrowLeft className="h-5 w-5" />
                </Button>
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
                    <Shield className="h-5 w-5" />
                </div>
                <div className="flex-1">
                    <h2 className="text-xl font-semibold text-foreground">
                        {isNew ? 'Create Role' : formData.name || 'Edit Role'}
                    </h2>
                    <p className="text-sm text-muted-foreground">
                        {isNew
                            ? 'Define a new role for your users'
                            : `Configure role settings and permissions`}
                    </p>
                </div>
                <Button onClick={handleSubmit} disabled={isSaving}>
                    {isSaving ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                        <Save className="mr-2 h-4 w-4" />
                    )}
                    Save
                </Button>
            </div>

            {/* Full width form */}
            <form onSubmit={handleSubmit} className="space-y-6">
                <div className="rounded-lg border border-border bg-card p-6">
                    <h3 className="mb-4 font-medium text-foreground">
                        Role Details
                    </h3>

                    {/* Name */}
                    <div className="mb-4 space-y-2">
                        <Label htmlFor="name">
                            Name <span className="text-red-500">*</span>
                        </Label>
                        <Input
                            id="name"
                            type="text"
                            value={formData.name}
                            onChange={(e) =>
                                setFormData({ ...formData, name: e.target.value })
                            }
                            placeholder="e.g., Content Editor"
                        />
                        {errors.name && (
                            <p className="text-sm text-red-500">{errors.name}</p>
                        )}
                    </div>

                    {/* Description */}
                    <div className="mb-4 space-y-2">
                        <Label htmlFor="description">Description</Label>
                        <Textarea
                            id="description"
                            value={formData.description}
                            onChange={(e) =>
                                setFormData({ ...formData, description: e.target.value })
                            }
                            rows={3}
                            placeholder="Describe what this role is for..."
                        />
                    </div>

                    {/* Parent Role */}
                    <div className="space-y-2">
                        <Label htmlFor="parent">Parent Role</Label>
                        <Select
                            value={formData.parent || '_none'}
                            onValueChange={(value) =>
                                setFormData({
                                    ...formData,
                                    parent: value === '_none' ? null : value,
                                })
                            }
                        >
                            <SelectTrigger>
                                <SelectValue placeholder="No parent (top-level role)" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="_none">No parent (top-level role)</SelectItem>
                                {availableParents?.map((r) => (
                                    <SelectItem key={r.id} value={r.id}>
                                        {r.name}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <p className="text-xs text-muted-foreground">
                            Child roles inherit permissions from their parent roles
                        </p>
                    </div>
                </div>

                {/* Policies Section */}
                {!isNew && (
                    <div className="rounded-lg border border-border bg-card p-6">
                        <div className="mb-4 flex items-center justify-between">
                            <h3 className="font-medium text-foreground">
                                Linked Policies
                            </h3>
                            <Button
                                type="button"
                                variant="secondary"
                                size="sm"
                                onClick={() => setShowPolicyModal(true)}
                            >
                                <Plus className="mr-1 h-4 w-4" />
                                Add Policy
                            </Button>
                        </div>

                        {linkedPolicies?.length === 0 ? (
                            <p className="text-sm text-muted-foreground">
                                No policies linked to this role yet.
                            </p>
                        ) : (
                            <div className="space-y-2">
                                {linkedPolicies?.map((policy) => {
                                    const access = accessEntries?.find(
                                        (a) => getPolicyId(a) === policy.id
                                    );
                                    return (
                                        <div
                                            key={policy.id}
                                            className="flex items-center justify-between rounded-lg border border-border bg-background p-3"
                                        >
                                            <div className="flex items-center gap-3">
                                                <div className="flex h-8 w-8 items-center justify-center rounded bg-primary/10 text-primary">
                                                    <Shield className="h-4 w-4" />
                                                </div>
                                                <div>
                                                    <div className="font-medium text-foreground">
                                                        {policy.name}
                                                    </div>
                                                    {policy.admin_access && (
                                                        <span className="text-xs text-amber-600">
                                                            Admin Access
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="icon"
                                                onClick={() =>
                                                    access && handleRemovePolicy(access.id)
                                                }
                                                className="text-muted-foreground hover:bg-red-100 hover:text-red-600"
                                            >
                                                <X className="h-4 w-4" />
                                            </Button>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}
            </form>

            {/* Add Policy Modal */}
            <Dialog open={showPolicyModal} onOpenChange={setShowPolicyModal}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Add Policy</DialogTitle>
                    </DialogHeader>

                    {availablePolicies?.length === 0 ? (
                        <p className="text-sm text-muted-foreground">
                            All policies are already linked to this role.
                        </p>
                    ) : (
                        <div className="max-h-64 space-y-2 overflow-y-auto">
                            {availablePolicies?.map((policy) => (
                                <Button
                                    key={policy.id}
                                    variant="outline"
                                    onClick={() => handleAddPolicy(policy.id)}
                                    className="flex w-full items-center justify-start gap-3 h-auto p-3"
                                >
                                    <Shield className="h-5 w-5 text-primary" />
                                    <div className="text-left">
                                        <div className="font-medium text-foreground">
                                            {policy.name}
                                        </div>
                                        {policy.description && (
                                            <div className="text-xs text-muted-foreground">
                                                {policy.description}
                                            </div>
                                        )}
                                    </div>
                                </Button>
                            ))}
                        </div>
                    )}

                    <div className="flex justify-end">
                        <Button variant="outline" onClick={() => setShowPolicyModal(false)}>
                            Cancel
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
